package ru.it_mozaika.sender.js.application.frames.mainPage

import io.ktor.client.call.*
import io.ktor.http.*
import js.core.jso
import kotlinx.datetime.*
import mui.material.*
import mui.material.styles.Theme
import mui.material.styles.TypographyVariant
import mui.system.SxProps
import mui.system.responsive
import mui.system.sx
import muix.pickers.AdapterMoment
import muix.pickers.DateTimePicker
import muix.pickers.LocalizationProvider
import oldcomponents.reactEChartsCore
import react.*
import ru.it_mozaika.components.AvatarButton
import ru.it_mozaika.sender.common.models.SubscriptionListDaysStats
import ru.it_mozaika.sender.js.application.Http
import ru.it_mozaika.sender.js.application.components.StoresSelect
import ru.it_mozaika.sender.js.application.context.RequestResultContext
import ru.it_mozaika.sender.js.application.context.useStore
import ru.it_mozaika.sender.js.application.context.useSubscriptionLists
import ru.it_mozaika.sender.js.application.convertIntoLocalFormat
import ru.it_mozaika.sender.js.application.frames.MainCard
import ru.it_mozaika.sender.js.application.moment
import web.cssom.*
import kotlin.math.abs

val MainPage = FC<Props> {
    val storeContext = useStore()
    val subContext = useSubscriptionLists()
    useEffect(storeContext.currentStore) {
        subContext.addAllLists()
    }
    MainCard {
        sx {
            paddingTop = 5.px
            overflow = Overflow.visible
        }
        CardHeader {
            title = Stack.create {
                sx {
                    alignItems = AlignItems.center
                }
                direction = responsive(StackDirection.row)
                spacing = responsive(2)
                Typography {
                    variant = TypographyVariant.h2
                    +"Аналитика по рассылкам"
                }
                StoresSelect()
            }
        }
        Divider()
        CardContent {
            Stack {
                direction = responsive(StackDirection.column)
                spacing = responsive(2)
                Grid {
                    sx {
                        height = 100.pct
                    }
                    item = true
                    asDynamic().xs = 12
                    commonChart()
                }
                Divider()
                Grid {
                    sx {
                        height = 100.pct
                    }
                    item = true
                    asDynamic().xs = 12
                    dynamicChart()
                }
            }
        }
    }
}

val commonChart = VFC {
    val subContext = useSubscriptionLists()
    val storeContext = useStore()
    val requestResultContext = useContext(RequestResultContext)
    var periodStart by useState(Clock.System.now().minus(7, DateTimeUnit.DAY, TimeZone.UTC))
    val period = useMemo(periodStart) {
        val list = mutableListOf<String>()
        var date = periodStart.toLocalDateTime(TimeZone.UTC).date
        val currentDate = Clock.System.now().toLocalDateTime(TimeZone.UTC).date
        while (date <= currentDate) {
            list += date.toString().convertIntoLocalFormat() ?: date.toString()
            val newDate = date.plus(1, DateTimeUnit.DAY)
            date = newDate
        }
        list
    }.toTypedArray()
    var subscriptionCount by useState(mapOf<String, Int>())
    var subscriptionStats by useState(mapOf<String, List<SubscriptionListDaysStats>>())
    var inProcess by useState(false)
    var invalidPeriodStart by useState(false)
    val (data, minValue, maxValue) = useMemo(subscriptionStats, subscriptionCount, period) {
        var min = Int.MAX_VALUE
        var max = Int.MIN_VALUE
        val series = subscriptionStats.map { (id, stats) ->
            var count = subscriptionCount[id] ?: 0
            val statsMap = stats.associateBy { it.day }
            val dataList = period.sortedArrayDescending().map { date ->
                val dayStats = statsMap[date]
                val dateChanges = if (dayStats != null) {
                    dayStats.subscribe - dayStats.unsubscribe
                } else {
                    0
                }
                count -= dateChanges
                if (count < min) {
                    min = count
                }
                if (count > max) {
                    max = count
                }
                Pair(count + dateChanges, date)
            }.sortedBy { it.second }.map { it.first }.toTypedArray()
            jso<dynamic> {
                this.name = subContext.lists.find { it.id == id }?.name
                type = "line"
                data = dataList
                ariaStyle = jso {}
            }

        }.toTypedArray()
        Triple(series, min, max)
    }
    fun getCount() {
        inProcess = true
        Http.post(
            path = "/subscribers/lists/stats/count",
            body = subContext.currentIds.toList(),
        ) { response ->
            if (response.status != HttpStatusCode.OK) {
                requestResultContext.handleRequestRespond(response)
            } else {
                subscriptionCount = response.body()
            }
            inProcess = false
        }
    }

    fun getStats() {
        inProcess = true
        Http.post(
            path = "/subscribers/lists/stats",
            body = subContext.currentIds.toList(),
            query = mapOf("dayStart" to periodStart.toLocalDateTime(TimeZone.UTC).date.toString())
        ) { response ->
            if (response.status != HttpStatusCode.OK) {
                requestResultContext.handleRequestRespond(response)
            } else {
                subscriptionStats = response.body()
            }
            inProcess = false
        }
    }
    useEffect(periodStart, storeContext.currentStore, subContext.currentIds) {
        getCount()
        getStats()
    }
    Card {
        sx {
            overflow = Overflow.visible
            height = 100.pct
        }
        CardHeader {
            title = if (inProcess) CircularProgress.create {
                color = CircularProgressColor.secondary
            } else Typography.create {
                variant = TypographyVariant.h4
                +"Количество подписок"
            }
            action = Stack.create {
                direction = responsive(StackDirection.row)
                spacing = responsive(2)
                LocalizationProvider {
                    dateAdapter = AdapterMoment
                    adapterLocale = "ru"
                    DateTimePicker {
                        asDynamic().sx = jso<SxProps<Theme>> {
                            marginTop = 16.px
                        }
                        asDynamic().timezone = ReactNode("system")
                        asDynamic().label = ReactNode("Начало периода")
                        asDynamic().value = moment(periodStart.toJSDate()) ?: moment()

                        asDynamic().onChange = { newValue: dynamic ->
                            if (newValue.toString() == "Invalid date" || newValue == None.none) {
                                invalidPeriodStart = true
                            } else {
                                invalidPeriodStart = false
                                val localDate = LocalDateTime(
                                    newValue.year() as Int,
                                    (newValue.month() as Int) + 1,
                                    newValue.date() as Int,
                                    newValue.hour() as Int,
                                    newValue.minute() as Int,
                                    newValue.second() as Int
                                )
                                periodStart = localDate.toInstant(TimeZone.UTC)
                            }
                        }
                    }
                }
            }
        }
        reactEChartsCore {
            option = jso {
                tooltip = jso {
                    trigger = "axis"
                    axisPointer = jso {
                        this.type = "cross"
                    }
                }
                legend = jso {
                    bottom = 0
                    this.data =
                        useMemo(subContext.lists) { subContext.lists.map { it.name } }.toTypedArray()
                }
                grid = jso {
                    left = 0
                    bottom = 70
                    top = 40
                    containLabel = true
                }
                xAxis = jso {
                    type = "category"
                    name = "Дата"
                    boundaryGap = false
                    this.data = period
                }
                yAxis = jso {
                    type = "value"
                    name = "Подписки"
                    min = minValue
                    max = maxValue
                }
                series = data
            }
        }
    }
}

val dynamicChart = VFC {
    val storeContext = useStore()
    val requestResultContext = useContext(RequestResultContext)
    val subContext = useSubscriptionLists()
    var periodStart by useState(Clock.System.now().minus(7, DateTimeUnit.DAY, TimeZone.UTC))
    var periodEnd by useState(Clock.System.now())
    var period by useState(arrayOf<String>())
    var subscriptionStats by useState(mapOf<String, List<SubscriptionListDaysStats>>())
    var selectedSubscriptionId by useState("5e42cfbdbbbe5a01224bc4cc")
    var invalidPeriodStart by useState(false)
    var invalidPeriodEnd by useState(false)
    var inProcess by useState(false)
    val (data, minValue, maxValue) = useMemo(subscriptionStats) {
        val stats = subscriptionStats[selectedSubscriptionId]
        var min = Int.MAX_VALUE
        var max = Int.MIN_VALUE
        val series = listOf("Подписалось", "Отписалось").map { status ->
            val statsMap = stats?.associateBy { it.day }
            val dataList = period.sortedArrayDescending().map { date ->
                val dayStats = statsMap?.get(date)
                val dateChanges = if (dayStats != null) {
                    when (status) {
                        "Подписалось" -> dayStats.subscribe
                        "Отписалось" -> dayStats.unsubscribe
                        else -> 0
                    }
                } else {
                    0
                }
                if (dateChanges < min) {
                    min = dateChanges
                }
                if (dateChanges > abs(max)) {
                    max = dateChanges
                }
                Pair(dateChanges, date)
            }.sortedBy { it.second }.map { it.first }.toTypedArray()
            jso<dynamic> {
                this.name = status
                type = "bar"
                color = when (status) {
                    "Отписалось" -> "red"
                    "Подписалось" -> "blue"
                    else -> "green"
                }
                data = dataList
                ariaStyle = jso {}
            }
        }.toTypedArray()
        Triple(series, min, max)
    }
    fun getStats() {
        inProcess = true
        Http.post(
            path = "/subscribers/lists/stats",
            body = listOf(selectedSubscriptionId),
            query = mapOf(
                "dayStart" to periodStart.toLocalDateTime(TimeZone.UTC).date.toString(),
                "periodEnd" to periodEnd.toLocalDateTime(
                    TimeZone.UTC
                ).date.toString()
            )
        ) { response ->
            if (response.status != HttpStatusCode.OK) {
                requestResultContext.handleRequestRespond(response)
            } else {
                subscriptionStats = response.body()
            }
            inProcess = false
        }
    }

    fun getPeriod() {
        val list = mutableListOf<String>()
        var date = periodStart.toLocalDateTime(TimeZone.UTC).date
        val end = periodEnd.toLocalDateTime(TimeZone.UTC).date
        while (date <= end) {
            list += date.toString().convertIntoLocalFormat() ?: date.toString()
            val newDate = date.plus(1, DateTimeUnit.DAY)
            date = newDate
        }
        period = list.toTypedArray()
    }
    useEffect(storeContext.currentStore, selectedSubscriptionId) {
        getStats()
        getPeriod()
    }
    Card {
        sx {
            overflow = Overflow.visible
            height = 100.pct
        }
        CardHeader {
            title = Stack.create {
                sx {
                    alignItems = AlignItems.center
                }
                spacing = responsive(2)
                direction = responsive(StackDirection.row)
                if (!inProcess) Typography {
                    variant = TypographyVariant.h4
                    +"Динамика подписок"
                } else CircularProgress {
                    color = CircularProgressColor.secondary
                }
                FormControl {
                    sx { minWidth = 400.px }
                    color = FormControlColor.secondary
                    InputLabel {
                        color = FormLabelColor.secondary
                        +"Подписка"
                    }
                    Select {
                        color = InputBaseColor.secondary

                        labelId = "store-select-label"
                        id = "store-select"
                        value = selectedSubscriptionId
                        label = ReactNode("Подписка:")
                        onChange = { e, _ ->
                            selectedSubscriptionId = e.target.value
                        }
                        subContext.lists.forEach { list ->
                            MenuItem {
                                value = list.id
                                +list.name
                            }
                        }
                    }
                }
            }
            action = Stack.create {
                direction = responsive(StackDirection.row)
                spacing = responsive(2)
                LocalizationProvider {
                    dateAdapter = AdapterMoment
                    adapterLocale = "ru"
                    DateTimePicker {
                        asDynamic().sx = jso<SxProps<Theme>> {
                            marginTop = 16.px
                        }
                        asDynamic().timezone = ReactNode("system")
                        asDynamic().label = ReactNode("Начало периода")
                        asDynamic().value = moment(periodStart.toJSDate()) ?: moment()
                        asDynamic().onChange = { newValue: dynamic ->
                            if (newValue.toString() == "Invalid date" || newValue == None.none) {
                                invalidPeriodStart = true
                            } else {
                                invalidPeriodStart = false
                                val localDate = LocalDateTime(
                                    newValue.year() as Int,
                                    (newValue.month() as Int) + 1,
                                    newValue.date() as Int,
                                    newValue.hour() as Int,
                                    newValue.minute() as Int,
                                    newValue.second() as Int
                                )
                                periodStart = localDate.toInstant(TimeZone.UTC)
                            }
                        }
                    }
                }
                LocalizationProvider {
                    dateAdapter = AdapterMoment
                    adapterLocale = "ru"
                    DateTimePicker {
                        asDynamic().sx = jso<SxProps<Theme>> {
                            marginTop = 16.px
                        }
                        asDynamic().timezone = ReactNode("system")
                        asDynamic().label = ReactNode("Конец периода")
                        asDynamic().value = moment(periodEnd.toJSDate()) ?: moment()
                        asDynamic().onChange = { newValue: dynamic ->
                            if (newValue.toString() == "Invalid date" || newValue == None.none) {
                                invalidPeriodEnd = true
                            } else {
                                invalidPeriodEnd = false
                                val localDate = LocalDateTime(
                                    newValue.year() as Int,
                                    (newValue.month() as Int) + 1,
                                    newValue.date() as Int,
                                    newValue.hour() as Int,
                                    newValue.minute() as Int,
                                    newValue.second() as Int
                                )
                                periodEnd = localDate.toInstant(TimeZone.UTC)
                            }
                        }
                    }
                    AvatarButton {
                        tooltipTitle = "Обновить"
                        mui.icons.material.UpdateSharp()
                        onClick = {
                            getStats()
                            getPeriod()
                        }
                    }
                }
            }
        }
        reactEChartsCore {
            option = jso {
                tooltip = jso {
                    trigger = "axis"
                    axisPointer = jso {
                        this.type = "shadow"
                    }
                }
                legend = jso {
                    top = 0
                    this.data = listOf("Подписалось", "Отписалось").toTypedArray()
                }
                grid = jso {
                    left = 0
                    bottom = 50
                    top = 40
                    containLabel = true
                }
                xAxis = jso {
                    type = "category"
                    name = "Дата"
                    this.data = period
                }
                yAxis = jso {
                    type = "value"
                    name = "Подписки"
                    min = minValue
                    max = maxValue
                }
                series = data
            }
        }
    }
}